import { shapeTools, type ShapeTool } from "../native/shapes";
import { setNativeIcon } from "./native-icons";

const labels: Record<ShapeTool, string> = {
  rectangle: "Прямоугольник",
  ellipse: "Эллипс",
  polygon: "Треугольник",
};

/** Remembers the visible shape tool. Opening its menu never changes the scene. */
export class NativeShapeTools {
  private trigger = document.getElementById("native-shapes")!;
  private menu = document.getElementById("native-shape-menu")!;
  private current: ShapeTool = "rectangle";
  private touch: { id: number; x: number; y: number; time: number } | null =
    null;
  private ignoreClickUntil = 0;
  private narrow = matchMedia("(max-width: 1100px)");

  constructor(
    private root: HTMLElement,
    private activate: (tool: ShapeTool) => void,
    private beforeOpen: () => void,
  ) {
    for (const tool of shapeTools)
      document.getElementById("native-" + tool)!.onclick = (event) => {
        this.current = tool;
        if (event.detail > 0 && this.menu.contains(document.activeElement))
          (document.activeElement as HTMLElement).blur();
        this.close(event.detail === 0);
        activate(tool);
      };
    this.trigger.onclick = (event) => {
      if (performance.now() < this.ignoreClickUntil) return;
      if (
        this.narrow.matches ||
        (event as PointerEvent).pointerType === "touch"
      ) {
        if (!this.close()) this.open();
      } else {
        this.close();
        activate(this.current);
      }
    };
    this.trigger.ondblclick = (event) => {
      event.preventDefault();
      this.open();
    };
    this.trigger.oncontextmenu = (event) => {
      event.preventDefault();
      if (!this.close()) this.open();
    };
    this.trigger.onkeydown = (event) => {
      if (["ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        this.open(true);
      }
    };
    this.menu.onkeydown = (event) => {
      const buttons = Array.from(this.menu.querySelectorAll("button"));
      let index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        index =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? buttons.length - 1
              : (index +
                  (event.key === "ArrowDown" ? 1 : -1) +
                  buttons.length) %
                buttons.length;
        buttons[index]?.focus();
      } else if (event.key === "Tab") this.close(true);
    };
    this.trigger.onpointerdown = (event) => {
      this.ignoreClickUntil = 0;
      if (event.pointerType !== "touch" || this.touch) return;
      this.touch = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
      };
      this.trigger.setPointerCapture(event.pointerId);
    };
    this.trigger.onpointerup = (event) => {
      const start = this.touch;
      if (!start || event.pointerId !== start.id) return;
      this.touch = null;
      const dx = event.clientX - start.x,
        dy = event.clientY - start.y;
      if (
        performance.now() - start.time <= 700 &&
        dy <= -24 &&
        Math.abs(dy) >= Math.abs(dx) * 1.15
      ) {
        this.ignoreClickUntil = performance.now() + 800;
        this.open();
      }
    };
    this.trigger.onpointercancel = this.trigger.onlostpointercapture = () =>
      (this.touch = null);
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!(event.target as HTMLElement).closest("#native-shape-group"))
          this.close();
      },
      true,
    );
    window.addEventListener("resize", () => this.close());
    window.addEventListener("blur", () => {
      this.touch = null;
      this.close();
    });
  }
  private open(keyboard = false) {
    this.beforeOpen();
    this.menu.hidden = false;
    this.root.dataset.shapeMenu = "open";
    this.trigger.setAttribute("aria-expanded", "true");
    if (keyboard) document.getElementById("native-" + this.current)!.focus();
  }
  get opened() {
    return !this.menu.hidden;
  }
  close(restoreFocus = false) {
    if (this.menu.hidden) return false;
    if (restoreFocus || this.menu.contains(document.activeElement))
      this.trigger.focus();
    this.menu.hidden = true;
    this.root.dataset.shapeMenu = "closed";
    this.trigger.setAttribute("aria-expanded", "false");
    return true;
  }
  render(tool: string) {
    if (shapeTools.includes(tool as ShapeTool))
      this.current = tool as ShapeTool;
    setNativeIcon(this.trigger, this.current);
    this.trigger.setAttribute("aria-pressed", String(tool === this.current));
    this.trigger.setAttribute("aria-label", "Фигуры: " + labels[this.current]);
    this.trigger.title =
      "Фигуры: " +
      labels[this.current] +
      " · Двойной щелчок или правая кнопка — меню";
    for (const choice of shapeTools)
      document
        .getElementById("native-" + choice)!
        .setAttribute("aria-checked", String(choice === this.current));
  }
}
